import React, { useEffect } from 'react';
import { Dialog, DialogContent } from '@leadsnavi/ui-kit';
import { ModalComponentProps } from '@/components/moda-lInvoker';
import { AuthContent } from './index';
import type { AuthStep } from './types';
import { useAuth } from '@/hooks/useAuth';
import styles from './auth-content.module.scss';
import classNames from 'classnames';

/**
 * AuthContent 弹窗组件的 Props
 */
export interface AuthContentModalProps extends ModalComponentProps {
  /** 初始步骤 */
  step?: AuthStep;
  /** 初始邮箱 */
  email?: string;
  /** 邮件链接中的加密参数（用于验证邮箱和重置密码） */
  cs?: string;
  /** 弹窗关闭时的回调 */
  onClose?: () => void;
  /** 登录成功回调 */
  onLoginSuccess?: () => void;
  /** 注册成功回调 */
  onSignUpSuccess?: () => void;
  /** 展示变体：full 含左侧品牌区，form-only 仅表单区（移动端用） */
  variant?: 'full' | 'form-only';
  /** 是否显示关闭按钮，移动端传 false 禁止用户主动关闭 */
  showCloseButton?: boolean;
  /** 是否允许点击遮罩/按 ESC 关闭，移动端传 false */
  closable?: boolean;
}

/**
 * AuthContent 弹窗组件
 * 将 AuthContent 包装在 Dialog 中，用于 ModalService
 * 集成 useAuth hook 处理完整的认证流程
 */
export const AuthContentModal: React.FC<AuthContentModalProps> = ({
  open,
  visible,
  onCancel,
  onClose,
  onLoginSuccess,
  onSignUpSuccess,
  step: initialStep = 'signup',
  email: initialEmail = '',
  cs,
  variant = 'full',
  showCloseButton = true,
  closable = true,
}) => {
  const isOpen = open ?? visible ?? false;

  // 使用 useAuth hook 处理认证逻辑
  const auth = useAuth({
    initialStep,
    initialEmail,
    cs,
    onLoginSuccess: () => {
      onLoginSuccess?.();
      handleClose();
    },
    onSignUpSuccess: () => {
      onSignUpSuccess?.();
      handleClose();
    },
    onClose: () => {
      handleClose();
    },
  });

  // 处理关闭事件
  const handleClose = () => {
    onClose?.();
    onCancel?.();
  };

  // 如果有 cs 参数且是重置密码步骤，设置 cs 参数
  useEffect(() => {
    if (cs && isOpen) {
      if (initialStep === 'reset-password') {
        // 设置 cs 参数，等待用户输入新密码
        auth.setCs(cs);
      }
    }
  }, [cs, isOpen, initialStep]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) {
    return null;
  }

  const dialogContentClassName = classNames(
    'bg-transparent border-none shadow-none p-[0px] loginModalContent',
    styles.loginModalContent,
    variant === 'form-only' ? 'min-w-0 max-w-[440px] w-full h-full' : 'min-w-[1000px]'
  );

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className={dialogContentClassName}
        onInteractOutside={closable ? undefined : e => e.preventDefault()}
        onEscapeKeyDown={closable ? undefined : e => e.preventDefault()}
      >
        <AuthContent
          step={auth.step}
          email={auth.email}
          errors={auth.errors}
          loading={auth.loading}
          resendCountdown={auth.resendCountdown}
          onStepChange={auth.onStepChange}
          onSignUpSubmit={auth.onSignUpSubmit}
          onLoginSubmit={auth.onLoginSubmit}
          onForgotPasswordSubmit={auth.onForgotPasswordSubmit}
          onResetPasswordSubmit={auth.onResetPasswordSubmit}
          onResendClick={auth.onResendClick}
          googleAuthUrl={auth.googleAuthUrl}
          onPrivacyPolicyClick={auth.onPrivacyPolicyClick}
          onForgotPasswordClick={auth.onForgotPasswordClick}
          variant={variant}
          showCloseButton={showCloseButton}
          onClose={handleClose}
        />
      </DialogContent>
    </Dialog>
  );
};

export default AuthContentModal;
